import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { defaultInstance } from "../api/httpClient";
import { toaster } from "baseui/toast";

// A single open/close block for a weekday.
export interface ProductTimeRange { open: string; close: string }

// Pricing tier (mirrors service.booking_options).
export interface ProductBookingOption {
  id: string;
  label_en: string;
  label_es: string;
  short_desc_en?: string;
  short_desc_es?: string;
  price: number;
  currency: string;
  min_amount?: number;
  max_amount?: number;
}

export interface ProductRestrictions {
  min_age?: number;
  requires_id?: boolean;
  min_party_size?: number;
  max_party_size?: number;
  availability?: { start: string; end: string; status: string; reason?: string }[];
}

// Product interface based on the API response structure
export interface Product {
  id: number;
  name: string;
  description?: string;
  weight?: string;
  price: number;
  payment_gateway?: 'carnet' | 'portal';
  icon_name?: string;
  business_id?: string;
  product_class_id?: number;
  attribute?: string;
  available_on?: string;
  updated_at?: string;
  // ── Availability / conditions config (JSONB, mirrors the service table) ──
  operating_hours?: Record<string, ProductTimeRange | ProductTimeRange[]>;
  restrictions?: ProductRestrictions;
  max_capacity?: number;
  booking_window_start?: string;
  booking_window_end?: string;
  duration?: string;
  settings?: { intervals?: { duration?: string; max_per_slot?: number; start_padding?: string; end_padding?: string } };
  booking_options?: ProductBookingOption[];
  images?: string[];
  categories?: string[];
  // ── Offer wizard fields (added by add_offer_wizard_fields.sql) ──
  offer_type?: string;
  offer_subtype?: string;
  name_en?: string;
  name_es?: string;
  short_description_en?: string;
  short_description_es?: string;
  description_en?: string;
  description_es?: string;
  currency?: string;
  pricing_unit?: string;
  included_guests?: number;
  inventory?: number;
  extra_fees?: Array<{ label: string; amount: string | number }>;
  plans?: Array<{ name: string; price: string | number; billing: string }>;
  duration_type?: string;
  qr_enabled?: boolean;
  tables?: Array<{ name: string; seats: string | number }>;
  location?: { name?: string; city?: string; postal?: string };
  details?: Record<string, any>;
  includes?: string[];
  bring?: string[];
  booking_policy?: Record<string, any>;
  is_active?: boolean;
  allow_promo?: boolean;
}

export interface ProductResponse {
  data: Product[];
  total_pages: number;
  page: number;
  total_elements: number;
}

// Hook to fetch products for a specific business
export const useProducts = (businessId: string | null) => {
  return useQuery<Product[], Error>({
    queryKey: ['products', businessId],
    queryFn: async () => {
      if (!businessId) throw new Error('Business ID is required');
      
      const response = await defaultInstance<ProductResponse>({
        url: '/product',
        method: 'GET',
        params: {
          business_id: businessId
        }
      });
      
      // The API returns the array directly, not wrapped in { data: [...] }
      const apiResponse = response.data;
      
      // Check if it's already an array (direct response)
      if (Array.isArray(apiResponse)) {
        return apiResponse;
      }
      
      // Otherwise, try to access .data property (paginated response)
      return Array.isArray((apiResponse as any).data) ? (apiResponse as any).data : [];
    },
    enabled: !!businessId
  });
};

// Hook to fetch ALL products (no business filter, for admins)
export const useAllProducts = () => {
  return useQuery<Product[], Error>({
    queryKey: ['products', 'all'],
    queryFn: async () => {
      const response = await defaultInstance<ProductResponse>({
        url: '/product',
        method: 'GET',
      });
      
      const apiResponse = response.data;
      if (Array.isArray(apiResponse)) return apiResponse;
      return Array.isArray((apiResponse as any).data) ? (apiResponse as any).data : [];
    },
  });
};

// Create product mutation
export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Product>) => {
      return await defaultInstance({
        url: '/product',
        method: 'POST',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toaster.positive('Product created successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to create product', { autoHideDuration: 3000 });
    },
  });
};

// Update product mutation
export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Product> }) => {
      return await defaultInstance({
        url: `/product/${id}`,
        method: 'PATCH',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toaster.positive('Product updated successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to update product', { autoHideDuration: 3000 });
    },
  });
};

// Delete product mutation
export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      return await defaultInstance({
        url: `/product/${id}`,
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toaster.positive('Product deleted successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to delete product', { autoHideDuration: 3000 });
    },
  });
};
